/**
 * LayoutResizableElement  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { isString } from "@adapptio/lib-shared";
import { LayoutState_Column, LayoutState_Panel, LayoutState_Row, LayoutState_Tabs } from "./LayoutState";
import throttle from "lodash.throttle";
import { useLayoutElement } from "./useLayoutElement";
import { ResizeElement } from "../Resize/ResizeElement";
import { ResizeHandler } from "../Resize/ResizeTypes";
import { useForkRef } from "@adapptio/lib-editor-ui";
import { useEnhancedEffect } from "../../hooks/useEnhancedEffect";

export interface ILayoutResizableElement
	extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	state: LayoutState_Row | LayoutState_Column | LayoutState_Panel | LayoutState_Tabs;
	minSize?: number;
}

const parseSizeString = (size: string): { value: number; unit: string } => {
	const { value, unit } = size.match(/(?<value>^\d+(\.\d+)?)(?<unit>\D+)/)?.groups || {};
	const numValue = parseFloat(value);

	return {
		value: numValue,
		unit
	};
};

export const LayoutResizableElement = React.forwardRef<HTMLDivElement, ILayoutResizableElement>(
	({ state, className, style, minSize, children, ...props }, ref) => {
		const el = useLayoutElement(state);
		const elementRef = useRef<HTMLDivElement>();
		const forkRef = useForkRef(elementRef, ref);
		const [ resizing, setResizing ] = useState(false);
		const canResize = !el.isHidden && (!el.isExpandable || el.isExpanded);

		const baseClass = BEM("edt-layout", "element", {
			resizable: true,
			resizing
		});

		const getElementDimensions = useCallback<() => { width: number; height: number }>(() => {
			const width = elementRef.current.offsetWidth;
			const height = elementRef.current.offsetHeight;

			return { width, height };
		}, []);

		const elSizeSetter = (size: number | string) => {
			el.setSize(size);
		};

		const setElSize = useMemo(() => throttle(elSizeSetter), []);

		const handleResizeStart = useCallback<ResizeHandler>(() => {
			if (!canResize) {
				return;
			}

			setResizing(true);
		}, [ canResize ]);

		const handleResize = useCallback<ResizeHandler>(
			({ offset, direction, newSize }) => {
				if (!canResize) {
					return;
				}

				const { value: prevSize, unit } = isString(el.size)
					? parseSizeString(el.size)
					: { value: el.size, unit: null };

				const dimensions = getElementDimensions();

				const sizeInPixels = direction.vertical ? dimensions.height : dimensions.width;

				// px size
				if (unit === "px") {
					const newSizePx = Math.max(sizeInPixels + offset, minSize);

					setElSize(`${newSizePx}px`);

					return;
				}

				if (sizeInPixels === minSize) {
					return;
				}

				// some other size
				if (unit) {
					const pxSizeValue = prevSize / sizeInPixels;
					const newSizeUnit = Math.max(prevSize + offset * pxSizeValue, minSize);
					const size = unit ? `${newSizeUnit}${unit}` : newSizeUnit;

					if (size !== el.size) {
						setElSize(size);
					}

					return;
				}

				if (newSize >= 1) {
					setElSize(newSize);
				}
			},
			[ el.size, canResize ]
		);

		const handleResizeStop = useCallback<ResizeHandler>(() => {
			if (!canResize) {
				return;
			}

			setResizing(false);
		}, [ canResize ]);

		if (className) {
			baseClass.add(className);
		}

		useLayoutEffect(() => {
			requestAnimationFrame(() => {
				const { width, height } = elementRef?.current?.getBoundingClientRect();
				elementRef?.current?.style.setProperty("--layout-width", `${width}px`);
				elementRef?.current?.style.setProperty("--layout-height", `${height}px`);
			});
		}, [ el.size ]);

		return (
			<ResizeElement
				{...props}
				id={el.id}
				size={el.size}
				minSizePx={minSize}
				resizeDisabled={!canResize}
				onResizeStart={handleResizeStart}
				onResize={handleResize}
				onResizeStop={handleResizeStop}
				Component="div"
				className={baseClass.toClassName()}
				style={style}
				ref={forkRef}
			>
				{children}
			</ResizeElement>
		);
	}
);

LayoutResizableElement.displayName = "LayoutResizableElement";

LayoutResizableElement.defaultProps = {
	minSize: 0
};
